/**
 * SelectMenu — a dropdown whose open list this product actually controls.
 *
 * WHY NOT A NATIVE <select>
 * -------------------------
 * A native `<select>`'s open option list is drawn by the browser. Chromium on
 * Windows — which is ora in Edge/Chrome AND the add-in in WebView2 — honours
 * `option { background-color }` and `option:checked`, but ignores
 * `option:hover`: the bar that follows the pointer uses the browser's own
 * highlight and is not reachable from CSS. `app/common/styles/native-select.css`
 * takes that as far as it goes and it is not far enough — the highlight stays
 * dark next to a light form.
 *
 * So the rows here are ordinary elements. That is the whole point: they take
 * the same `primary-gray-light` ramp as every other list in the product, and
 * they take it in both themes of every browser.
 *
 * IT MATCHES `OldMortgageeSelect` BY CONSTRUCTION
 * ----------------------------------------------
 * That component is the pattern this generalises — button + `role="listbox"`,
 * same trigger classes, same row classes, same 200-hover-over-100-selected rule
 * so the two stay distinguishable while the pointer is on the selected row. It
 * is deliberately NOT refactored onto this component: it carries a hover
 * details panel, a "Not listed" escape and a manual-entry mode, none of which
 * belong in a general dropdown. Generalising it would mean adding three props
 * that exactly one caller uses. If those features ever become common, move them
 * here then — not before.
 *
 * WHAT CALLERS MAY AND MAY NOT CHANGE
 * -----------------------------------
 * `className` lands on the WRAPPER only, because width genuinely is a call-site
 * concern (one caller is `max-w-[58%]`, another is `flex-1`). The trigger and
 * the rows are not overridable, and that asymmetry is on purpose: the product
 * had five hand-rolled sliders in three sizes precisely because each one could
 * be tweaked where it was used. Layout is the caller's; palette is not.
 */

import { useEffect, useRef, useState } from "react";
import CustomTooltip from "~/common/components/CustomTooltip";

export interface SelectMenuOption {
  value: string;
  label: string;
  /** The row's hover explanation, where one exists — shown in ora's
   *  CustomTooltip, not as a native `title`. */
  title?: string;
}

export interface SelectMenuProps {
  value: string;
  onChange: (next: string) => void;
  options: readonly SelectMenuOption[];
  /** Shown when `value` matches no option. Also the empty-selection row. */
  placeholder?: string;
  disabled?: boolean;
  /** Accessible name for the trigger and the listbox. */
  label: string;
  /** Wrapper classes — width and flex only. See the header. */
  className?: string;
  id?: string;
  "data-testid"?: string;
  /**
   * Prefix for the per-row test ids, yielding `${prefix}-${value}`. Defaults
   * to `${testId}-option`. It exists so a caller migrating OFF a native
   * <select> keeps the ids its existing tests already query — the rows are the
   * same rows, and renaming them would turn a styling change into a test
   * rewrite for no behavioural reason.
   */
  optionTestIdPrefix?: string;
}

export default function SelectMenu({
  value,
  onChange,
  options,
  placeholder = "Select…",
  disabled = false,
  label,
  className = "",
  id,
  "data-testid": testId,
  optionTestIdPrefix,
}: SelectMenuProps) {
  const [open, setOpen] = useState(false);
  const [dropUp, setDropUp] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);

  //: The panel's own max height (max-h-48 = 12rem = 192px) plus the mt-1 gap.
  //: Used to decide which side of the trigger it opens on, so it stays in
  //: sync with the class below.
  const PANEL_MAX_PX = 192 + 4;

  // Open UPWARD when there is not room below.
  //
  // The panel is absolutely positioned inside whatever scroll container the
  // form gives it, so a menu opening downward from a row near the bottom is
  // clipped, and the container grows a scrollbar to reach it (reported
  // 2026-09-22 on the run-start form's Oversight row, which is the LAST row
  // above a sticky footer DASH the worst case, and the common one).
  //
  // Measured on OPEN rather than on mount: the same control can sit at
  // different heights as the form above it grows, so a placement decided once
  // would be wrong the moment a mortgagee action is added.
  useEffect(() => {
    if (!open) return;
    const rect = rootRef.current?.getBoundingClientRect();
    if (!rect) return;
    const spaceBelow = window.innerHeight - rect.bottom;
    const spaceAbove = rect.top;
    // Only flip when below genuinely will not fit AND above is better; a tie
    // keeps the conventional downward direction.
    setDropUp(spaceBelow < PANEL_MAX_PX && spaceAbove > spaceBelow);
  }, [open]);

  // Close on outside click and on Escape. Bound only while OPEN so a page of
  // these does not carry a document listener each.
  useEffect(() => {
    if (!open) return;
    const onDocClick = (e: MouseEvent) => {
      if (!rootRef.current?.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onDocClick);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDocClick);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const selected = options.find((o) => o.value === value) ?? null;
  const optionPrefix =
    optionTestIdPrefix ?? (testId ? `${testId}-option` : undefined);

  return (
    <div className={`relative ${className}`} ref={rootRef}>
      {/* Story 28633: the pane's ONE control height (34px) and 13px input text —
          launchDensity's COMPACT_CONTROL_HEIGHT / COMPACT_INPUT_TEXT, kept literal
          because common/components must not import from common/workflows. */}
      <button
        id={id}
        type="button"
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={label}
        onClick={() => setOpen((v) => !v)}
        data-testid={testId}
        className="flex h-[34px] w-full items-center justify-between rounded-md border border-primary-gray-light-300 bg-base-white px-2.5 text-left text-[13px] leading-[19px] disabled:bg-primary-gray-light-50 disabled:text-primary-gray-light-700 disabled:opacity-60"
      >
        <span
          className={`truncate ${
            selected
              ? "text-primary-gray-light-900"
              : "text-primary-gray-light-500"
          }`}
        >
          {selected ? selected.label : placeholder}
        </span>
        <span aria-hidden="true" className="ms-2 text-primary-gray-light-500">
          ▾
        </span>
      </button>
      {open && (
        <div
          role="listbox"
          aria-label={label}
          className={`absolute z-20 w-full rounded-lg border border-primary-gray-light-300 bg-base-white shadow-lg ${
            dropUp ? "bottom-full mb-1" : "top-full mt-1"
          }`}
          data-testid={testId ? `${testId}-listbox` : undefined}
        >
          <ul className="max-h-48 overflow-y-auto">
            {options.map((o) => {
              const row = (
                <button
                  type="button"
                  role="option"
                  aria-selected={o.value === value}
                  onClick={() => {
                    onChange(o.value);
                    setOpen(false);
                  }}
                  // Same ramp as OldMortgageeSelect's rows. Hover sits one step
                  // above selected so the two stay distinguishable while the
                  // pointer is on the selected row.
                  className={`w-full px-3 py-1.5 text-left text-sm hover:bg-primary-gray-light-200 ${
                    o.value === value
                      ? "bg-primary-gray-light-100 font-medium"
                      : ""
                  }`}
                  data-testid={optionPrefix ? `${optionPrefix}-${o.value}` : undefined}
                >
                  {o.label}
                </button>
              );
              return (
                <li key={o.value}>
                  {o.title ? (
                    // ora's tooltip, not a native title (story 28633). The
                    // row keeps its label as its name; the explanation is its
                    // description (`describeChild`, node title — see
                    // FieldHelpTooltip for why not a string).
                    <CustomTooltip
                      id={`${id ?? testId ?? "select-menu"}-option-${o.value}`}
                      title={<span>{o.title}</span>}
                      describeChild
                      placement="left"
                      arrow
                      isOnModal
                    >
                      {row}
                    </CustomTooltip>
                  ) : (
                    row
                  )}
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </div>
  );
}
